/**
 * Gộp hai hồ sơ khách trùng ở web quầy (STT 2.5, ADR-091, docs/plans/stt-2-5-gop-ho-so.md mục 12).
 *
 *   `ManGopChon` — chọn hồ sơ còn lại (gợi ý "Có thể trùng" + ô tìm).
 *   `ManGopXem`  — xem trước hai cột + (nếu cần) chọn số + xác nhận 2 bước → gộp.
 *
 * Hai màn con của tờ khách (`ToGoiViKhach`). Không import từ GoiVi.tsx (GoiVi.tsx import tệp này) — tránh vòng.
 * Máy chủ quyết định gộp được hay không và KIỂM LẠI khi gộp; chữ lý do là câu của máy chủ.
 */
import { ngayVnCua } from '@sapa/shared'
import { AlertCircle, Check, ShieldAlert, TriangleAlert, UserRound } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import {
  type Api,
  ApiFailure,
  type CustomerHit,
  hienSdt,
  type KetQuaGop,
  type TomTatGop,
  tenKhach,
  type UngVienTrung,
  type XemTruocGop,
} from './api.ts'
import {
  type Chieu,
  canTickXacNhanZalo,
  cauHauQuaSo,
  cauLoiGop,
  chieuBiChanViTien,
  chieuKia,
  chieuMacDinh,
  chuSoSauGop,
  coTheDoiChieu,
  coTheTiepTuc,
  type DongCanhBao,
  dongCanhBao,
  ketQuaChieu,
  ngayVietGop,
  nhanNguon,
  soCuaLuaChon,
  thanGop,
  viecKhiBamGop,
} from './gop-khach.ts'
import { khoaChoNoiDung } from './nhap-khach-hien.ts'
import { GK } from './text/gop-khach.ts'
import { T } from './text/vi.ts'
import { ThongBao, Xuong } from './ui.tsx'

// ============================================================================= 1. Chọn hồ sơ còn lại

interface DongChon {
  id: string
  ten: string
  phone: string | null
  coZalo: boolean | null
  /** ISO lần cuối tới (nếu có) — để quầy phân biệt hai hồ sơ cùng tên. */
  lanCuoi: string | null
}

const tuUngVien = (u: UngVienTrung): DongChon => ({
  id: u.id,
  ten: u.fullName,
  phone: u.phone,
  coZalo: u.coZalo,
  lanCuoi: u.lastVisitAt,
})
const tuKetQuaTim = (h: CustomerHit): DongChon => ({
  id: h.id,
  ten: h.fullName,
  phone: h.phone,
  coZalo: null,
  lanCuoi: h.lastVisitAt ?? null,
})

/** Gợi ý KHÔNG lặp lại hồ sơ đã hiện ở "Kết quả tìm" (cùng một người hiện hai lần làm quầy tưởng có hai hồ sơ). */
function goiYConLai(goiY: DongChon[], ketQua: DongChon[] | null): DongChon[] {
  if (ketQua === null) return goiY
  const da = new Set(ketQua.map((k) => k.id))
  return goiY.filter((g) => !da.has(g.id))
}

function DongHoSo({ d, onChon }: { d: DongChon; onChon: (id: string) => void }) {
  return (
    <button type="button" className="gop-dong" onClick={() => onChon(d.id)}>
      <span className="vong-icon-nho">
        <UserRound size={20} />
      </span>
      <span className="gop-dong-chu">
        <b>{tenKhach(d.ten)}</b>
        <span>
          {d.phone === null || d.phone === '' ? GK.chuaCoSo : hienSdt(d.phone)}
          {d.coZalo === true && ` · ${GK.coZalo}`}
          {d.lanCuoi !== null &&
            ` · ${GK.lanCuoiGoiY(ngayVietGop(d.lanCuoi, (i) => ngayVnCua(new Date(i))))}`}
        </span>
      </span>
    </button>
  )
}

export function ManGopChon({
  api,
  customerId,
  onChon,
  onLoi,
}: {
  api: Api
  customerId: string
  onChon: (id: string) => void
  onLoi: (s: string) => void
}) {
  const [q, setQ] = useState('')
  const [goiY, setGoiY] = useState<DongChon[] | null>(null)
  const [ketQua, setKetQua] = useState<DongChon[] | null>(null)
  const [dangTim, setDangTim] = useState(false)
  // Lượt tìm có số riêng: kết quả của lượt cũ trả chậm bị bỏ (không nhảy về chữ đã xoá).
  const luot = useRef(0)

  useEffect(() => {
    let con = true
    api
      .goiYGop(customerId)
      .then((r) => {
        if (con) setGoiY(r.khach.filter((u) => u.id !== customerId).map(tuUngVien))
      })
      .catch((e: unknown) => {
        if (!con) return
        setGoiY([])
        onLoi(e instanceof ApiFailure ? e.messageVi : GK.loiChung)
      })
    return () => {
      con = false
    }
  }, [api, customerId, onLoi])

  useEffect(() => {
    const chu = q.trim()
    // Ít nhất 2 chữ (tên) hoặc 3 số cuối — máy chủ cũng đòi vậy; dưới ngưỡng thì không gọi.
    const soCuoi = /^[\d\s.+-]+$/.test(chu)
    const du = soCuoi ? chu.replace(/\D/g, '').length >= 3 : [...chu].length >= 2
    if (!du) {
      luot.current += 1
      setKetQua(null)
      setDangTim(false)
      return
    }
    const so = ++luot.current
    setDangTim(true)
    const hen = setTimeout(() => {
      api
        .findCustomer(chu)
        .then((r) => {
          if (so !== luot.current) return
          setKetQua(r.customers.filter((h) => h.id !== customerId).map(tuKetQuaTim))
        })
        .catch((e: unknown) => {
          if (so !== luot.current) return
          setKetQua([])
          onLoi(e instanceof ApiFailure ? e.messageVi : GK.loiChung)
        })
        .finally(() => {
          if (so === luot.current) setDangTim(false)
        })
    }, 250)
    return () => clearTimeout(hen)
  }, [q, api, customerId, onLoi])

  return (
    <div className="to-than gop-than">
      <p className="chu-phu gop-chu">{GK.giaiThichChon}</p>
      <div className="truong">
        <label className="nhan" htmlFor="gop-tim">
          {GK.oTim}
        </label>
        <input
          id="gop-tim"
          className="o-nhap"
          type="text"
          autoComplete="off"
          maxLength={60}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {q.trim() !== '' && (ketQua !== null || dangTim) && (
        <div className="muc-to">
          <h3>{GK.ketQuaTim}</h3>
          {ketQua === null ? (
            <p className="chu-phu gop-chu">{GK.dangTim}</p>
          ) : ketQua.length === 0 ? (
            <p className="chu-phu gop-chu">{GK.khongThay}</p>
          ) : (
            <div className="gop-ds">
              {ketQua.map((d) => (
                <DongHoSo key={d.id} d={d} onChon={onChon} />
              ))}
            </div>
          )}
        </div>
      )}

      <div className="muc-to">
        <h3>{GK.coTheTrung}</h3>
        {goiY === null ? (
          <Xuong cao={64} />
        ) : goiYConLai(goiY, ketQua).length === 0 ? (
          <p className="chu-phu gop-chu">{goiY.length === 0 ? GK.khongGoiY : GK.goiYDaORTren}</p>
        ) : (
          <div className="gop-ds">
            {goiYConLai(goiY, ketQua).map((d) => (
              <DongHoSo key={d.id} d={d} onChon={onChon} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================================================= 2–3. Xem trước + xác nhận

function TheGop({
  t,
  nhan,
  vai,
  laGiu,
}: {
  t: TomTatGop
  /** Nguồn: "Hồ sơ đang mở" / "Hồ sơ kia". */
  nhan: string
  /** Vai sau khi gộp ("Giữ lại" / "Gộp vào rồi ẩn"); `null` khi không gộp được (không hứa gì). */
  vai: string | null
  laGiu: boolean
}) {
  const homNay = (iso: string) => ngayVnCua(new Date(iso))
  return (
    <section className={`gop-the ${vai === null ? '' : laGiu ? 'giu' : 'bo'}`} aria-label={nhan}>
      <span className="gop-the-nhan">{nhan}</span>
      {vai !== null && <span className="gop-the-vai">{vai}</span>}
      <b className="gop-the-ten">{tenKhach(t.ten)}</b>
      <ul className="gop-the-ds">
        <li>{t.phone === null || t.phone === '' ? GK.chuaCoSo : hienSdt(t.phone)}</li>
        {t.ngaySinh !== null && <li>{GK.ngaySinh(ngayVietGop(t.ngaySinh, homNay))}</li>}
        <li>{GK.soLich(t.soLich, t.soLichSapToi)}</li>
        <li>{t.lanCuoi === null ? GK.chuaTungToi : GK.lanCuoi(ngayVietGop(t.lanCuoi, homNay))}</li>
        <li>{t.coZalo ? GK.coZalo : GK.chuaZalo}</li>
        <li className={t.coTien ? 'gop-co-tien' : ''}>{t.coTien ? GK.coTien : GK.chuaTien}</li>
      </ul>
      {t.luuY !== null && t.luuY.trim() !== '' && <p className="gop-the-luu-y">{t.luuY}</p>}
    </section>
  )
}

/** Danh sách cảnh báo có thứ bậc: NẶNG = đỏ + đậm + khiên, NHẸ = vàng + tam giác. */
function KhoiCanhBao({ ds, tieuDe }: { ds: DongCanhBao[]; tieuDe: string }) {
  if (ds.length === 0) return null
  return (
    <section className="gop-canh-bao" aria-label={tieuDe}>
      <h3>{tieuDe}</h3>
      <ul>
        {ds.map((d) => (
          <li key={`${d.ma}-${d.chu}`} className={`gop-cb ${d.muc}`}>
            {d.muc === 'nang' ? <ShieldAlert size={20} /> : <TriangleAlert size={20} />}
            <span>{d.chu}</span>
          </li>
        ))}
      </ul>
    </section>
  )
}

export function ManGopXem({
  api,
  customerId,
  khacId,
  onDong,
  onMoMucCho,
  onLoi,
  onXong,
}: {
  api: Api
  /** Hồ sơ đang mở (a). */
  customerId: string
  /** Hồ sơ vừa chọn (b). */
  khacId: string
  /** Không gộp được → nút chính "Đóng": về màn tổng của tờ. */
  onDong: () => void
  /** Ca "đang chờ xác nhận Zalo": đóng tờ để tới mục Chờ xác nhận (đầu tab Tìm khách). */
  onMoMucCho?: () => void
  onLoi: (s: string) => void
  /** `cauThem`: việc kế tiếp (vd nhắc bấm "Đúng người") — người gọi ghép vào câu báo. */
  onXong: (r: KetQuaGop, tenGiu: string, cauThem: string | null) => void
}) {
  const [x, setX] = useState<XemTruocGop | null>(null)
  const [chieu, setChieu] = useState<Chieu>('a')
  const [soGiu, setSoGiu] = useState<'giu' | 'bo' | null>(null)
  const [buoc, setBuoc] = useState<'xem' | 'xac-nhan'>('xem')
  const [dang, setDang] = useState(false)
  // Có Zalo ở bất kỳ bên nào: ô tích RIÊNG "khách đang ở tiệm, đã mở Zalo cho tôi xem" (N1 + N2). Đổi chiều / số
  // được chọn thì tích lại (không để ô tích cũ che hậu quả mới).
  const [daTickZalo, setDaTickZalo] = useState(false)
  // Bấm "Gộp hai hồ sơ" khi chưa tích: không tắt nút, cuộn tới ô tích và nhắc.
  const [nhacTich, setNhacTich] = useState(false)
  const refTich = useRef<HTMLLabelElement>(null)
  // Nạp lại xem trước (lỗi KHONG_GOP_DUOC: dữ liệu vừa đổi ở máy khác).
  const [lanNap, setLanNap] = useState(0)
  // Bấm "Tiếp tục" khi cần chọn số mà chưa chọn: không bấm được âm thầm — cuộn tới câu hỏi và nhắc.
  const [nhacSo, setNhacSo] = useState(false)
  const refSo = useRef<HTMLDivElement>(null)
  const dangRef = useRef(false)
  // Khoá chống lặp theo LỰA CHỌN (hai hồ sơ + số được chọn): bấm lại sau lỗi mạng = cùng khoá (không gộp hai lần);
  // đổi lựa chọn = khoá mới. KHÔNG sinh khoá mới mỗi lần bấm (lessons 24/09).
  const khoa = useRef<{ noiDung: string; khoa: string } | null>(null)

  useEffect(() => {
    // `lanNap` tăng khi máy chủ báo "chưa gộp được" → nạp lại xem trước (dữ liệu vừa đổi).
    void lanNap
    let con = true
    setX(null)
    api
      .xemTruocGop(customerId, khacId)
      .then((r) => {
        if (!con) return
        setX(r)
        setChieu(chieuMacDinh(r))
        setSoGiu(null)
        setDaTickZalo(false)
        setNhacTich(false)
        setBuoc('xem')
      })
      .catch((e: unknown) => {
        if (!con) return
        onLoi(e instanceof ApiFailure ? e.messageVi : GK.loiXemTruoc)
      })
    return () => {
      con = false
    }
  }, [api, customerId, khacId, onLoi, lanNap])

  if (x === null) {
    return (
      <div className="to-than gop-than">
        <Xuong cao={160} />
        <Xuong cao={160} />
      </div>
    )
  }

  const k = ketQuaChieu(x, chieu)
  const giu = chieu === 'a' ? x.a : x.b
  const bo = chieu === 'a' ? x.b : x.a
  const soDaChon = soCuaLuaChon(k, soGiu)
  const tiep = coTheTiepTuc(k, soGiu)
  const lyDo = k.duocGop ? null : (x.lyDoChung ?? k.lyDo)
  const canTick = canTickXacNhanZalo(x)

  function doiChieu() {
    setChieu(chieuKia(chieu))
    setSoGiu(null)
    setNhacSo(false)
    setDaTickZalo(false)
    setNhacTich(false)
  }

  function tiepTuc() {
    if (!tiep) {
      setNhacSo(true)
      refSo.current?.scrollIntoView({ block: 'center' })
      return
    }
    setBuoc('xac-nhan')
  }

  async function gop() {
    if (dangRef.current || x === null) return
    if (viecKhiBamGop(canTickXacNhanZalo(x), daTickZalo) === 'nhac-tich') {
      // Nút không tắt: nhắc đúng việc cần làm và đưa ô tích vào tầm mắt (không "nút chết" không giải thích).
      setNhacTich(true)
      refTich.current?.scrollIntoView({ block: 'center' })
      return
    }
    // `daXacNhanZalo`: máy chủ đòi khi có Zalo ở bất kỳ bên nào — cờ nằm TRONG thân nên đổi tích = đổi khoá chống lặp.
    const than = { ...thanGop(x, chieu, soGiu), daXacNhanZalo: canTickXacNhanZalo(x) && daTickZalo }
    dangRef.current = true
    setDang(true)
    khoa.current = khoaChoNoiDung(khoa.current, JSON.stringify(than), () => crypto.randomUUID())
    try {
      const r = await api.gopKhach(than, khoa.current.khoa)
      const choXacNhan = k.canhBao.some((c) => c.ma === 'cho-xac-nhan')
      onXong(r, k.ketQua?.ten ?? giu.ten, choXacNhan ? GK.nhacDungNguoi : null)
    } catch (e) {
      if (e instanceof ApiFailure) {
        const c = cauLoiGop(e)
        if (!c.giuKhoa) khoa.current = null
        // Máy chủ nói "chưa gộp được" (dữ liệu vừa đổi): nạp lại xem trước — nút không còn sáng theo bản cũ.
        if (e.code === 'KHONG_GOP_DUOC') setLanNap((n) => n + 1)
        onLoi(c.cau)
      } else {
        onLoi(T.serverFailed)
      }
    } finally {
      dangRef.current = false
      setDang(false)
    }
  }

  const tenGiu = tenKhach(k.ketQua?.ten ?? giu.ten)
  const tenBo = tenKhach(bo.ten)
  const dsDay = dongCanhBao(k.canhBao, tenKhach(giu.ten), tenBo)
  const dsNgan = dongCanhBao(k.canhBao, tenKhach(giu.ten), tenBo, { ngan: true })
  // Máy tự đảo chiều vì hồ sơ ĐANG MỞ có tiền / gói nên không ẩn được → nói vì sao.
  const daDaoChieu = chieu === 'b' && chieuBiChanViTien(ketQuaChieu(x, 'a'))
  const choXacNhanZaloBlock =
    !k.duocGop && (k.maLyDo === 'cho-xac-nhan-zalo' || x.giuA.maLyDo === 'cho-xac-nhan-zalo')

  if (buoc === 'xac-nhan') {
    return (
      <>
        <div className="to-than gop-than">
          <section className="gop-xac-nhan">
            <h3>{GK.tieuDeXacNhan}</h3>
            <p>{GK.cauXacNhan(tenBo)}</p>
            <ul className="gop-the-ds">
              <li>
                {GK.theGiu}: <b>{tenGiu}</b>
              </li>
              <li>
                {GK.theBo}: <b>{tenBo}</b>
              </li>
            </ul>
          </section>
          <KhoiCanhBao ds={dsNgan} tieuDe={GK.tieuNhacLai} />
          {canTick && (
            <label
              ref={refTich}
              className={`cong-tac-dong gop-tich ${nhacTich && !daTickZalo ? 'can-nhac' : ''}`}
            >
              <span className="chu">
                <b>{GK.xacNhanZalo}</b>
                {nhacTich && !daTickZalo && (
                  <span className="chu-loi" role="alert">
                    {GK.nhacXacNhanKhach}
                  </span>
                )}
              </span>
              <input
                type="checkbox"
                checked={daTickZalo}
                disabled={dang}
                onChange={(e) => {
                  setDaTickZalo(e.target.checked)
                  setNhacTich(false)
                }}
              />
            </label>
          )}
        </div>
        <footer className="to-chan hai-nut gop-chan">
          <button
            type="button"
            className="nut nut-vien nut-lon"
            disabled={dang}
            onClick={() => setBuoc('xem')}
          >
            {GK.quayLai}
          </button>
          <button
            type="button"
            className="nut nut-nguy-dac nut-lon"
            disabled={dang}
            onClick={() => void gop()}
          >
            {dang ? GK.dangGop : GK.nutGop}
          </button>
        </footer>
      </>
    )
  }

  const nhanGiu = nhanNguon(giu.id, x)
  const nhanBo = nhanNguon(bo.id, x)
  const vaiGiu = k.duocGop ? GK.vaiGiu : null
  const vaiBo = k.duocGop ? GK.vaiBo : null

  return (
    <>
      <div className="to-than gop-than">
        {/* Việc đầu tiên quầy cần biết — TRÊN hai thẻ: lý do chặn, rồi cảnh báo (nặng trước). */}
        {lyDo !== null && (
          <div className="gop-ly-do" role="alert">
            <AlertCircle size={22} />
            <div>
              <b>{GK.khongGopDuoc}</b>
              <p>{lyDo}</p>
              {choXacNhanZaloBlock && onMoMucCho !== undefined && (
                <button type="button" className="nut nut-vien nut-form" onClick={onMoMucCho}>
                  {GK.moMucCho}
                </button>
              )}
            </div>
          </div>
        )}
        {daDaoChieu && (
          <ThongBao loai="thong-tin" lon>
            {GK.maDaoChieu(tenKhach(x.b.ten))}
          </ThongBao>
        )}
        {k.duocGop && <KhoiCanhBao ds={dsDay} tieuDe={GK.tieuCanhBao} />}

        <div className="gop-cap">
          <TheGop t={giu} nhan={nhanGiu} vai={vaiGiu} laGiu />
          <TheGop t={bo} nhan={nhanBo} vai={vaiBo} laGiu={false} />
        </div>
        {coTheDoiChieu(x, chieu) && (
          <button type="button" className="nut nut-vien nut-form gop-nut" onClick={doiChieu}>
            {GK.doiChieu(tenBo)}
          </button>
        )}

        {k.duocGop && k.ketQua !== null && (
          <>
            {k.canChonSo && (
              <div className="muc-to" ref={refSo}>
                <h3>{GK.hoiSo}</h3>
                <fieldset className="gop-chon-so" aria-label={GK.hoiSo}>
                  {(['giu', 'bo'] as const).map((c) => {
                    const so =
                      c === 'giu' ? k.ketQua?.phoneTheoSoGiu.giu : k.ketQua?.phoneTheoSoGiu.bo
                    const chu = c === 'giu' ? tenKhach(giu.ten) : tenBo
                    return (
                      <button
                        key={c}
                        type="button"
                        className={`gop-lua-chon ${soGiu === c ? 'dang-chon' : ''}`}
                        aria-pressed={soGiu === c}
                        onClick={() => {
                          setSoGiu(c)
                          setNhacSo(false)
                        }}
                      >
                        <span className="gop-lua-so">{hienSdt(so ?? null)}</span>
                        <span className="gop-lua-chu">
                          {GK.soCuaHoSo(chu)}
                          <b className="gop-lua-vai">{c === 'giu' ? GK.vaiGiu : GK.vaiBo}</b>
                        </span>
                        {soGiu === c && <Check size={22} aria-label={GK.daChon} />}
                      </button>
                    )
                  })}
                </fieldset>
                <p
                  className={`gop-hau-qua ${nhacSo && soGiu === null ? 'chu-loi' : ''}`}
                  aria-live="polite"
                >
                  {nhacSo && soGiu === null ? GK.chonSoNhac : cauHauQuaSo(k, soGiu, hienSdt)}
                </p>
              </div>
            )}

            <section className="gop-sau">
              <h3>{GK.sauGop}</h3>
              <ul className="gop-the-ds">
                <li>
                  <b>{tenKhach(k.ketQua.ten)}</b>
                </li>
                <li>{chuSoSauGop(soDaChon, k.canChonSo, hienSdt)}</li>
                {k.ketQua.ngaySinh !== null && (
                  <li>
                    {GK.ngaySinh(ngayVietGop(k.ketQua.ngaySinh, (i) => ngayVnCua(new Date(i))))}
                  </li>
                )}
                <li>{GK.soLichSau(x.a.soLich + x.b.soLich)}</li>
                <li>{GK.nhanTin(k.ketQua.nhanTin)}</li>
                <li>{GK.luuAnh(k.ketQua.luuAnh)}</li>
                <li>{GK.zalo(k.ketQua.coZalo)}</li>
                {k.ketQua.luuY !== null && k.ketQua.luuY.trim() !== '' && (
                  <li className="gop-the-luu-y">{GK.luuYSau(k.ketQua.luuY)}</li>
                )}
              </ul>
            </section>
          </>
        )}
      </div>
      <footer className="to-chan gop-chan">
        {k.duocGop ? (
          <button
            type="button"
            className={`nut nut-chinh nut-lon ${tiep ? '' : 'gop-mo'}`}
            aria-disabled={!tiep}
            onClick={tiepTuc}
          >
            {GK.tiepTuc}
          </button>
        ) : (
          <button type="button" className="nut nut-vien nut-lon" onClick={onDong}>
            {GK.dong}
          </button>
        )}
      </footer>
    </>
  )
}
